/**
 * Security Vendor Directory service (VIS-199).
 *
 * Talks to the API Gateway only (`/api/v1/security-vendors`), which proxies
 * to vendor-service. Never calls the microservice port directly (L-002).
 */
import { apiClient } from './api';

export type SecurityDomain =
  | 'PHYSICAL_SECURITY'
  | 'CYBERSECURITY'
  | 'EXECUTIVE_PROTECTION'
  | 'SURVEILLANCE'
  | 'ACCESS_CONTROL'
  | 'INVESTIGATIONS'
  | 'THREAT_INTELLIGENCE'
  | 'GUARD_SERVICES'
  | 'SECURITY_CONSULTING'
  | 'PENETRATION_TESTING'
  | 'SIEM_SOC'
  | 'INCIDENT_RESPONSE';

export type ServiceRegion = 'NORTH_AMERICA' | 'EMEA' | 'APAC' | 'LATAM';

export type ComplianceCertification =
  | 'SOC2'
  | 'ISO_27001'
  | 'HITRUST'
  | 'FEDRAMP'
  | 'PCI_DSS'
  | 'GDPR'
  | 'HIPAA'
  | 'NIST_CSF'
  | 'CMMC';

export type SecurityVendorTier = 'ENTERPRISE' | 'MID_MARKET' | 'SMB';

export type SecurityVendorStatus =
  | 'NOMINATED'
  | 'VETTING'
  | 'TRIAL'
  | 'APPROVED'
  | 'RECOMMENDED'
  | 'ON_PROBATION'
  | 'DEPRECATED'
  | 'BLACKLISTED';

export type VendorContractStatus = 'ACTIVE' | 'EXPIRED' | 'UNDER_REVIEW' | 'NEW';

export type ReviewCadence = 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL';

export type SecurityVendorReminderType =
  | 'CONTRACT_EXPIRATION'
  | 'CERTIFICATION_EXPIRATION'
  | 'PERIODIC_REVIEW';

/** A lifecycle reminder row (VIS-203). */
export interface SecurityVendorReminder {
  id: string;
  vendorId: string;
  reminderType: SecurityVendorReminderType;
  status: 'SCHEDULED' | 'SENT' | 'SNOOZED' | 'CANCELLED';
  scheduledDate: string;
  message: string;
  recipientId?: string | null;
  sentAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** An audit-trail entry for a vendor record change (VIS-203). */
export interface VendorAuditEntry {
  id: string;
  action: string;
  resourceId: string | null;
  userId: string | null;
  /** Display name for `userId`, resolved by vendor-service; absent if the lookup failed. */
  userName?: string;
  oldValues: string | null;
  newValues: string | null;
  createdAt: string;
}

export interface VendorReview {
  id: string;
  vendorId: string;
  reviewerId: string;
  serviceQuality: number;
  responsiveness: number;
  costEffectiveness: number;
  technicalCapability: number;
  compliancePosture: number;
  overallRating: number;
  comments?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VendorDomainCoverage {
  id: string;
  vendorId: string;
  domain: SecurityDomain;
  region: ServiceRegion;
  isPrimary: boolean;
}

/** Regional coverage matrix (VIS-202). */
export interface CoverageMatrixVendor {
  id: string;
  name: string;
  compositeRating: number;
  reviewCount: number;
  status: SecurityVendorStatus;
  isPrimary: boolean;
}

export interface CoverageMatrixCell {
  region: ServiceRegion;
  domain: SecurityDomain;
  vendors: CoverageMatrixVendor[];
  topVendorId: string | null;
  isGap: boolean;
}

export interface CoverageMatrix {
  regions: ServiceRegion[];
  domains: SecurityDomain[];
  cells: CoverageMatrixCell[];
  summary: {
    totalCells: number;
    coveredCells: number;
    gapCells: number;
    gaps: Array<{ region: ServiceRegion; domain: SecurityDomain }>;
  };
}

export interface SecurityVendor {
  id: string;
  name: string;
  description?: string | null;
  logoUrl?: string | null;
  websiteUrl?: string | null;
  domains: SecurityDomain[];
  regions: ServiceRegion[];
  countries: string[];
  certifications: ComplianceCertification[];
  primaryContactName?: string | null;
  primaryContactEmail?: string | null;
  primaryContactPhone?: string | null;
  accountManagerName?: string | null;
  accountManagerEmail?: string | null;
  contractStatus?: VendorContractStatus | null;
  contractStartDate?: string | null;
  contractEndDate?: string | null;
  slaSummary?: string | null;
  vendorTier?: SecurityVendorTier | null;
  status: SecurityVendorStatus;
  reviewCadence?: ReviewCadence | null;
  nextReviewDate?: string | null;
  certificationExpiryDate?: string | null;
  compositeRating: number;
  reviewCount: number;
  verifiedRecommendedAt?: string | null;
  verifiedBy?: string | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  /** Display names for createdBy/updatedBy, resolved server-side (detail endpoint only). */
  createdByName?: string;
  updatedByName?: string;
  createdAt: string;
  updatedAt: string;
  reviews?: VendorReview[];
  coverage?: VendorDomainCoverage[];
}

export interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export type VendorSortField =
  | 'name'
  | 'createdAt'
  | 'updatedAt'
  | 'compositeRating'
  | 'reviewCount'
  | 'lastReviewedAt'
  | 'status';

/** Directory filters (VIS-201). Multi-select filters are arrays, serialized as CSV. */
export interface VendorFilters {
  search?: string;
  status?: SecurityVendorStatus[];
  vendorTier?: SecurityVendorTier[];
  domains?: SecurityDomain[];
  regions?: ServiceRegion[];
  certifications?: ComplianceCertification[];
  contractStatus?: VendorContractStatus[];
  country?: string;
  minRating?: number;
  sortBy?: VendorSortField;
  sortOrder?: 'asc' | 'desc';
}

export interface ListVendorsParams extends VendorFilters {
  page?: number;
  limit?: number;
}

/** A saved, per-user filter preset (VIS-201). */
export interface VendorFilterPreset {
  id: string;
  userId: string;
  name: string;
  filters: VendorFilters;
  createdAt: string;
  updatedAt: string;
}

/** Fields a client may set when creating/updating a vendor (server strips the rest). */
export type VendorFormInput = Partial<
  Pick<
    SecurityVendor,
    | 'name'
    | 'description'
    | 'logoUrl'
    | 'websiteUrl'
    | 'domains'
    | 'regions'
    | 'countries'
    | 'certifications'
    | 'primaryContactName'
    | 'primaryContactEmail'
    | 'primaryContactPhone'
    | 'accountManagerName'
    | 'accountManagerEmail'
    | 'contractStatus'
    | 'contractStartDate'
    | 'contractEndDate'
    | 'slaSummary'
    | 'vendorTier'
    | 'reviewCadence'
    | 'nextReviewDate'
    | 'certificationExpiryDate'
  >
>;

/** Review criteria a reviewer submits (each 1–5). overallRating is computed server-side. */
export interface ReviewInput {
  serviceQuality: number;
  responsiveness: number;
  costEffectiveness: number;
  technicalCapability: number;
  compliancePosture: number;
  comments?: string;
}

/** One point on the rating trend line (VIS-200 rating history). */
export interface RatingHistoryPoint {
  reviewId: string;
  date: string;
  overallRating: number;
  compositeAfter: number;
  reviewCount: number;
}

const BASE = '/api/v1/security-vendors';

/**
 * Serialize directory params to a query string. Arrays (multi-select filters) join
 * as CSV — the backend `csvEnum` parser accepts CSV or repeated keys; empty arrays
 * and empty/nullish scalars are omitted so they don't over-constrain the query.
 */
export function buildVendorQuery(params: ListVendorsParams): URLSearchParams {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {return;}
    if (Array.isArray(value)) {
      if (value.length > 0) {query.append(key, value.join(','));}
      return;
    }
    query.append(key, String(value));
  });
  return query;
}

class SecurityVendorService {
  async list(params: ListVendorsParams = {}): Promise<{ vendors: SecurityVendor[]; pagination: Pagination }> {
    const query = buildVendorQuery(params);
    const qs = query.toString();
    const response = await apiClient.get<{ vendors: SecurityVendor[]; pagination: Pagination }>(
      qs ? `${BASE}?${qs}` : BASE
    );
    if (response.success && response.data) {
      return {
        vendors: response.data.vendors ?? [],
        pagination: response.data.pagination ?? { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch vendors');
  }

  async get(id: string): Promise<SecurityVendor> {
    const response = await apiClient.get<SecurityVendor>(`${BASE}/${id}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch vendor');
  }

  async create(data: VendorFormInput): Promise<SecurityVendor> {
    const response = await apiClient.post<SecurityVendor>(BASE, data);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to create vendor');
  }

  async update(id: string, data: VendorFormInput): Promise<SecurityVendor> {
    const response = await apiClient.put<SecurityVendor>(`${BASE}/${id}`, data);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to update vendor');
  }

  async remove(id: string): Promise<void> {
    const response = await apiClient.delete<{ id: string; deleted: boolean }>(`${BASE}/${id}`);
    if (!response.success) {
      throw new Error(response.error?.message ?? 'Failed to delete vendor');
    }
  }

  // --- Rating & Review (VIS-200) ---

  async submitReview(id: string, data: ReviewInput): Promise<VendorReview> {
    const response = await apiClient.post<VendorReview>(`${BASE}/${id}/reviews`, data);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to submit review');
  }

  async listReviews(
    id: string,
    params: { page?: number; limit?: number; sortOrder?: 'asc' | 'desc' } = {}
  ): Promise<{ reviews: VendorReview[]; pagination: Pagination }> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {query.append(key, String(value));}
    });
    const qs = query.toString();
    const response = await apiClient.get<{ reviews: VendorReview[]; pagination: Pagination }>(
      qs ? `${BASE}/${id}/reviews?${qs}` : `${BASE}/${id}/reviews`
    );
    if (response.success && response.data) {
      return {
        reviews: response.data.reviews ?? [],
        pagination: response.data.pagination ?? { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch reviews');
  }

  async getRatingHistory(id: string): Promise<RatingHistoryPoint[]> {
    const response = await apiClient.get<{ points: RatingHistoryPoint[] }>(`${BASE}/${id}/rating-history`);
    if (response.success && response.data) {
      return response.data.points ?? [];
    }
    throw new Error(response.error?.message ?? 'Failed to fetch rating history');
  }

  async verify(id: string): Promise<SecurityVendor> {
    const response = await apiClient.post<SecurityVendor>(`${BASE}/${id}/verify`, {});
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to verify vendor');
  }

  // --- Lifecycle management (VIS-203) ---

  /** Transition a vendor to a new lifecycle status (enforced by the backend FSM). */
  async updateStatus(id: string, status: SecurityVendorStatus, comments?: string): Promise<SecurityVendor> {
    const response = await apiClient.post<SecurityVendor>(`${BASE}/${id}/status`, { status, comments });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to update vendor status');
  }

  async getReminders(id: string): Promise<SecurityVendorReminder[]> {
    const response = await apiClient.get<{ reminders: SecurityVendorReminder[] }>(`${BASE}/${id}/reminders`);
    if (response.success && response.data) {
      return response.data.reminders ?? [];
    }
    throw new Error(response.error?.message ?? 'Failed to fetch reminders');
  }

  async getAudit(
    id: string,
    params: { page?: number; limit?: number } = {}
  ): Promise<{ entries: VendorAuditEntry[]; pagination: Pagination }> {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {query.append(key, String(value));}
    });
    const qs = query.toString();
    const response = await apiClient.get<{ entries: VendorAuditEntry[]; pagination: Pagination }>(
      qs ? `${BASE}/${id}/audit?${qs}` : `${BASE}/${id}/audit`
    );
    if (response.success && response.data) {
      return {
        entries: response.data.entries ?? [],
        pagination: response.data.pagination ?? { total: 0, page: 1, limit: 20, totalPages: 0 },
      };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch audit trail');
  }

  // --- Saved filter presets (VIS-201) ---

  async listPresets(): Promise<VendorFilterPreset[]> {
    const response = await apiClient.get<{ presets: VendorFilterPreset[] }>(`${BASE}/filter-presets`);
    if (response.success && response.data) {
      return response.data.presets ?? [];
    }
    throw new Error(response.error?.message ?? 'Failed to fetch filter presets');
  }

  async createPreset(name: string, filters: VendorFilters): Promise<VendorFilterPreset> {
    const response = await apiClient.post<VendorFilterPreset>(`${BASE}/filter-presets`, { name, filters });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to save filter preset');
  }

  async updatePreset(
    id: string,
    data: { name?: string; filters?: VendorFilters }
  ): Promise<VendorFilterPreset> {
    const response = await apiClient.put<VendorFilterPreset>(`${BASE}/filter-presets/${id}`, data);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to update filter preset');
  }

  async deletePreset(id: string): Promise<void> {
    const response = await apiClient.delete<{ id: string; deleted: boolean }>(`${BASE}/filter-presets/${id}`);
    if (!response.success) {
      throw new Error(response.error?.message ?? 'Failed to delete filter preset');
    }
  }

  // --- Regional coverage matrix + export (VIS-202) ---

  async getCoverageMatrix(): Promise<CoverageMatrix> {
    const response = await apiClient.get<CoverageMatrix>(`${BASE}/coverage-matrix`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch coverage matrix');
  }

  /** Triggers a browser download of the matrix as CSV or PDF (via the gateway, L-002). */
  async exportCoverageMatrix(format: 'csv' | 'pdf'): Promise<void> {
    await apiClient.downloadFile(
      `${BASE}/coverage-matrix/export?format=${format}`,
      `coverage-matrix.${format}`,
    );
  }
}

export const securityVendorService = new SecurityVendorService();
export default securityVendorService;
